import type {
  AgentActions,
  AgentToolCall,
  LayerStyleRgba,
  LayerStyleUpdate,
} from './types'

export const LAYER_FILL_OPTIONS = [
  "greyscale",
  "jet",
  "hsv",
  "hot",
  "cool",
  "spring",
  "summer",
  "autumn",
  "winter",
  "copper",
  "YIGnBu",
  "greens",
  "YIOrRd",
  "bluered",
  "RdBu",
  "BuRd",
  "picnic",
  "rainbow",
  "portland",
  "blackbody",
  "earth",
  "electric",
  "viridis",
  "inferno",
  "magma",
  "plasma",
  "warm",
  "rainbow-soft",
  "bathymetry",
  "cdom",
  "chlorophyll",
  "density",
  "freesurface-blue",
  "freesurface-red",
  "oxygen",
  "par",
  "phase",
  "salinity",
  "temperature",
  "turbidity",
  "velocity-blue",
  "velocity-green",
  "cubehelix",
] as const

/** Accepted fills: sidebar options plus legacy aliases. */
export const VALID_LAYER_FILLS = new Set<string>([
  ...LAYER_FILL_OPTIONS,
  "heatmap", // legacy alias for jet
  "WIGnBu", // legacy spelling of YIGnBu
])

export const AGENT_TOOLS = [
  {
    type: "function" as const,
    function: {
      name: "add_component",
      description:
        "Add a new component node to the model graph. Returns the new node id and the canonical component name. " +
        "Provide a `ref` to bind the created node to a symbolic name (e.g. \"x1\") so later tool calls in the SAME response can reference it before its real id is known.",
      parameters: {
        type: "object",
        properties: {
          name: {
            type: "string",
            description:
              "Component name from the catalogue header (text before the colon), " +
              "e.g. 'Greater' or 'Numeric constant'. Do not use the optional " +
              "'Toolbox label' suffix like 'Greater (>)'.",
          },
          ref: {
            type: "string",
            description:
              "Optional symbolic ref (e.g. \"x1\", \"x2\") to bind this new node to. " +
              "Pass this same value as the id in later connect_nodes / set_control / " +
              "set_component_name / set_layer_style calls within this response; the editor substitutes " +
              "the real id once the node is created.",
          },
        },
        required: ["name"],
      },
    },
  },
  {
    type: "function" as const,
    function: {
      name: "remove_component",
      description: "Remove a node from the model graph by its id.",
      parameters: {
        type: "object",
        properties: {
          id: {
            type: ["integer", "string"],
            description:
              "The node to remove: a real numeric id from the snapshot, or a " +
              "symbolic ref (e.g. \"x1\") bound by an add_component call in this response.",
          },
        },
        required: ["id"],
      },
    },
  },
  {
    type: "function" as const,
    function: {
      name: "set_component_name",
      description:
        "Update the per-node namebar (the editable label shown above a node " +
        "body). Pass an empty string for `name` to clear the custom label " +
        "and fall back to the default component name.",
      parameters: {
        type: "object",
        properties: {
          id: {
            type: ["integer", "string"],
            description:
              "The node to rename: a real numeric id from the snapshot, or a " +
              "symbolic ref (e.g. \"x1\") bound by an add_component call in this response.",
          },
          name: { type: "string", description: "New label, or empty string to clear." },
        },
        required: ["id", "name"],
      },
    },
  },
  {
    type: "function" as const,
    function: {
      name: "set_control",
      description:
        "Update a control on a node. The control name should match what is " +
        "shown in the graph snapshot's 'Controls:' section (e.g. 'Dataset " +
        "Year', 'Value', 'Source', 'table' / 'tableEditor'). For dropdowns / " +
        "multi-selects, `value` may be the option's display name or its " +
        "underlying id; pass an array of names/ids for multi-select. For " +
        "text and numeric controls pass a string or number. For booleans " +
        "pass true/false. For Table controls pass either " +
        "{ rows: string[], columns: string[], cells: (number|null)[][] } " +
        "or a nested map { rowName: { colName: number, … }, … }. " +
        "For OpenStreetMap Land Use custom queries (`Build Query` / " +
        "`osmCustomQueries`) pass [{key, value, type}] — e.g. " +
        '[{"key":"landuse","value":"residential","type":"way"}] — or ' +
        '"key=value" / "[key=value]". Prefer Preset Categories for ' +
        "common land-use names. For a named place, use the OpenStreetMap " +
        "Place component and set Place / placeName to a name such as " +
        "\"Jakarta\". Match / placeMatch lists alternative boundaries after " +
        "the lookup runs.",
      parameters: {
        type: "object",
        properties: {
          id: {
            type: ["integer", "string"],
            description:
              "Target node: a real numeric id from the snapshot, or a symbolic " +
              "ref (e.g. \"x1\") bound by an add_component call in this response.",
          },
          control: {
            type: "string",
            description: "Control name as shown in the graph snapshot.",
          },
          value: {
            description:
              "New value. String/number for text/numeric, boolean for " +
              "checkboxes, string or option-name for dropdowns, string[] " +
              "for multi-select. For tables: {rows, columns, cells} or " +
              "{ rowName: { colName: number } }. For OSM custom queries: " +
              '[{key, value, type}] or "key=value".',
          },
        },
        required: ["id", "control", "value"],
      },
    },
  },
  {
    type: "function" as const,
    function: {
      name: "connect_nodes",
      description:
        "Wire one node's output socket into another node's input socket " +
        "(direction: from_id.from_output -> to_id.to_input). Socket names " +
        "are taken from the graph snapshot in the system prompt and may be " +
        "either the literal key or the displayed name.",
      parameters: {
        type: "object",
        properties: {
          from_id: {
            type: ["integer", "string"],
            description: "Source node: real numeric id from the snapshot, or a symbolic ref (e.g. \"x1\") from this response.",
          },
          from_output: { type: "string", description: "Source output socket name." },
          to_id: {
            type: ["integer", "string"],
            description: "Target node: real numeric id from the snapshot, or a symbolic ref (e.g. \"x2\") from this response.",
          },
          to_input: { type: "string", description: "Target input socket name." },
        },
        required: ["from_id", "from_output", "to_id", "to_input"],
      },
    },
  },
  {
    type: "function" as const,
    function: {
      name: "disconnect_nodes",
      description:
        "Remove the connection between from_id.from_output and to_id.to_input. " +
        "Both endpoints must already be wired together.",
      parameters: {
        type: "object",
        properties: {
          from_id: {
            type: ["integer", "string"],
            description: "Source node: real numeric id from the snapshot, or a symbolic ref from this response.",
          },
          from_output: { type: "string", description: "Source output socket name." },
          to_id: {
            type: ["integer", "string"],
            description: "Target node: real numeric id from the snapshot, or a symbolic ref from this response.",
          },
          to_input: { type: "string", description: "Target input socket name." },
        },
        required: ["from_id", "from_output", "to_id", "to_input"],
      },
    },
  },
  {
    type: "function" as const,
    function: {
      name: "set_layer_style",
      description:
        "Style a Map layer on the map view (not the graph node chrome). " +
        "`id` must be a Map layer node. Pick the fields that match the layer's " +
        "data type: numeric → `fill` (colormap from the fill enum — e.g. " +
        "\"viridis\", \"greyscale\", \"jet\", \"RdBu\"); boolean → `false_color` and/or " +
        "`true_color`; categorical → `label_colors` mapping each category " +
        "label to a colour. Colours may be hex (\"#rrggbb\") or RGBA arrays " +
        "([r,g,b] or [r,g,b,a] with 0–255 channels).",
      parameters: {
        type: "object",
        properties: {
          id: {
            type: ["integer", "string"],
            description:
              "Map layer node: a real numeric id from the snapshot, or a " +
              "symbolic ref (e.g. \"x1\") bound by an add_component call in this response.",
          },
          fill: {
            type: "string",
            enum: [...LAYER_FILL_OPTIONS],
            description:
              "Numeric colormap / fill mode. Must be one of the enum values " +
              "(same list as the map sidebar Fill mode dropdown).",
          },
          false_color: {
            description: "Boolean layer colour for false. Hex string or [r,g,b] / [r,g,b,a].",
          },
          true_color: {
            description: "Boolean layer colour for true. Hex string or [r,g,b] / [r,g,b,a].",
          },
          label_colors: {
            type: "object",
            description:
              "Categorical layer: object whose keys are category labels and " +
              "values are colours (hex or RGBA arrays).",
            additionalProperties: true,
          },
        },
        required: ["id"],
      },
    },
  },
  {
    type: "function" as const,
    function: {
      name: "set_layer_description",
      description:
        "Set the public description on a Map layer. It appears when someone " +
        "hovers that layer's info icon on the public map. `description` is " +
        "markdown (headings, links, and pictures such as ![caption](https://…)). " +
        "Pass an empty string to clear it. `id` must be a Map layer node.",
      parameters: {
        type: "object",
        properties: {
          id: {
            type: ["integer", "string"],
            description:
              "Map layer node: a real numeric id from the snapshot, or a " +
              "symbolic ref (e.g. \"x1\") bound by an add_component call in this response.",
          },
          description: {
            type: "string",
            description:
              "Markdown shown on the public info icon. Empty string clears the description.",
          },
        },
        required: ["id", "description"],
      },
    },
  },
  {
    type: "function" as const,
    function: {
      name: "search_projects",
      description:
        "Search other projects in the current team by name. Use this when " +
        "you want inspiration from similar models, to reuse an approach, or " +
        "to answer questions about how other projects are built. Returns " +
        "project id, name, and updated_at. Pass an empty query to list " +
        "projects. Follow up with `read_project_model` on a promising id. " +
        "Does NOT change the open canvas.",
      parameters: {
        type: "object",
        properties: {
          query: {
            type: "string",
            description:
              "Case-insensitive name substring to match. Omit or pass \"\" to list team projects.",
          },
        },
        required: [],
      },
    },
  },
  {
    type: "function" as const,
    function: {
      name: "read_project_model",
      description:
        "Load another project in this team and return its node graph as a " +
        "human-readable outline (nodes, connections, controls) — the same " +
        "format as the live canvas snapshot. Use after `search_projects` to " +
        "inspect how a project is built. Read-only; does not modify the " +
        "open editor.",
      parameters: {
        type: "object",
        properties: {
          project_id: {
            type: "integer",
            description: "Project id from `search_projects` (or known to the user).",
          },
        },
        required: ["project_id"],
      },
    },
  },
]


/** Colormap names accepted by numeric Map layer `fill` (see LAYER_FILL_OPTIONS). */

function clampChannel(n: number, max = 255): number {
  if (!Number.isFinite(n)) return 0
  return Math.max(0, Math.min(max, Math.round(n)))
}

/**
 * Parse a colour from agent tool args: hex (`#rgb` / `#rrggbb`),
 * `rgb()` / `rgba()`, or a numeric array `[r,g,b]` / `[r,g,b,a]`.
 */
export function parseAgentColor(
  raw: unknown
): { ok: true; color: LayerStyleRgba } | { ok: false; error: string } {
  if (Array.isArray(raw)) {
    if (raw.length < 3) {
      return { ok: false, error: "RGBA array needs at least [r, g, b]." }
    }
    const r = clampChannel(Number(raw[0]))
    const g = clampChannel(Number(raw[1]))
    const b = clampChannel(Number(raw[2]))
    const aRaw = raw.length >= 4 ? Number(raw[3]) : 1
    const a = Number.isFinite(aRaw) ? Math.max(0, Math.min(1, aRaw > 1 ? aRaw / 255 : aRaw)) : 1
    return { ok: true, color: [r, g, b, a] }
  }

  if (typeof raw !== "string") {
    return { ok: false, error: "Colour must be a hex string or [r,g,b,a] array." }
  }

  const s = raw.trim()
  const hex = s.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i)
  if (hex) {
    const h = hex[1]
    const full =
      h.length === 3
        ? h.split("").map(c => c + c).join("")
        : h
    return {
      ok: true,
      color: [
        parseInt(full.slice(0, 2), 16),
        parseInt(full.slice(2, 4), 16),
        parseInt(full.slice(4, 6), 16),
        1,
      ],
    }
  }

  const rgba = s.match(
    /^rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)(?:\s*,\s*([0-9.]+))?\s*\)$/i
  )
  if (rgba) {
    const aRaw = rgba[4] != null ? Number(rgba[4]) : 1
    const a = Number.isFinite(aRaw) ? Math.max(0, Math.min(1, aRaw > 1 ? aRaw / 255 : aRaw)) : 1
    return {
      ok: true,
      color: [
        clampChannel(Number(rgba[1])),
        clampChannel(Number(rgba[2])),
        clampChannel(Number(rgba[3])),
        a,
      ],
    }
  }

  return {
    ok: false,
    error: `Unrecognised colour "${s}". Use "#rrggbb" or [r,g,b,a].`,
  }
}

/**
 * Resolve a node-id tool argument that may be either a real numeric id
 * (number, or a digit string, optionally `#`-prefixed) or a symbolic plan
 * ref (e.g. "x1") bound earlier this turn by an `add_component` call's
 * `ref`. Lets the model batch a whole plan in one response — it wires
 * `connect_nodes(from_id="x1", …)` before "x1"'s real id exists, and we
 * substitute the real id here as each `add_component` populates `refMap`.
 */
export function resolveNodeId(
  raw: unknown,
  refMap: Map<string, number>
): { ok: true; id: number } | { ok: false; error: string } {
  if (typeof raw === "number" && Number.isFinite(raw)) return { ok: true, id: raw }
  if (typeof raw === "string") {
    const s = raw.trim().replace(/^#/, "")
    if (/^\d+$/.test(s)) return { ok: true, id: Number(s) }
    const real = refMap.get(s)
    if (real !== undefined) return { ok: true, id: real }
    return {
      ok: false,
      error:
        `Unknown node reference "${raw}". Pass a numeric id from the snapshot, ` +
        `or a ref (e.g. "x1") that an earlier add_component call in this same ` +
        `response bound via its \`ref\` argument.`,
    }
  }
  return { ok: false, error: "A node id is required (numeric id, or symbolic ref like \"x1\")." }
}

/**
 * Format a batch of tool calls into the programmatic plan notation used in
 * the system prompt (e.g. `x1 = add_component("Map layer")`,
 * `connect_nodes(x1.All -> x2.in)`). Surfaced to the chat as a `plan`
 * message for debugging so you can see exactly what the model intends to do
 * before it executes.
 */
export function formatToolCallsAsPlan(toolCalls: AgentToolCall[]): string {
  const idDisp = (v: unknown): string => {
    if (typeof v === "number") return `#${v}`
    if (typeof v === "string") {
      const s = v.trim().replace(/^#/, "")
      return /^\d+$/.test(s) ? `#${s}` : v.trim()
    }
    return String(v)
  }
  return toolCalls
    .map(call => {
      let a: any = {}
      try {
        a = call.function?.arguments ? JSON.parse(call.function.arguments) : {}
      } catch {
        return `${call.function?.name}(${call.function?.arguments ?? ""})`
      }
      switch (call.function?.name) {
        case "add_component":
          return `${a.ref ? `${a.ref} = ` : ""}add_component(${JSON.stringify(a.name ?? "")})`
        case "remove_component":
          return `remove_component(${idDisp(a.id)})`
        case "set_component_name":
          return `set_component_name(${idDisp(a.id)}, ${JSON.stringify(a.name ?? "")})`
        case "set_control":
          return `set_control(${idDisp(a.id)}, ${JSON.stringify(a.control ?? "")}, ${JSON.stringify(a.value)})`
        case "connect_nodes":
          return `connect_nodes(${idDisp(a.from_id)}.${a.from_output} -> ${idDisp(a.to_id)}.${a.to_input})`
        case "disconnect_nodes":
          return `disconnect_nodes(${idDisp(a.from_id)}.${a.from_output} -> ${idDisp(a.to_id)}.${a.to_input})`
        case "search_projects":
          return `search_projects(${JSON.stringify(a.query ?? "")})`
        case "read_project_model":
          return `read_project_model(${a.project_id})`
        default:
          return `${call.function?.name}(${call.function?.arguments ?? ""})`
      }
    })
    .join("\n")
}

/**
 * Dispatch a single tool call from the model and return both the JSON body
 * to send back as the `tool` message and a short human-readable label for
 * the chat UI. Errors are reported as `{ok: false, error}` payloads rather
 * than thrown so the model can read them and recover (e.g. retry with a
 * different component name).
 *
 * `refMap` carries the symbolic-ref → real-id bindings for the current turn:
 * `add_component` writes to it (when a `ref` is supplied) and the id-bearing
 * tools read from it, so a batched plan can wire nodes it just created.
 */
export async function executeAgentTool(
  call: AgentToolCall,
  actions: AgentActions | undefined,
  refMap: Map<string, number>
): Promise<{ content: string; label?: string }> {
  const name = call.function?.name
  let args: Record<string, unknown> = {}
  try {
    args = call.function?.arguments ? JSON.parse(call.function.arguments) : {}
  } catch {
    return {
      content: JSON.stringify({ ok: false, error: "Could not parse tool arguments as JSON." }),
      label: `Tool ${name} failed: bad arguments`,
    }
  }

  if (!actions) {
    return {
      content: JSON.stringify({ ok: false, error: "Editor is not ready yet — try again in a moment." }),
      label: `Tool ${name} skipped: editor not ready`,
    }
  }

  if (name === "add_component") {
    const componentName = typeof args.name === "string" ? args.name : ""
    if (!componentName) {
      return {
        content: JSON.stringify({ ok: false, error: "`name` is required." }),
        label: "add_component failed: missing name",
      }
    }
    const ref = typeof args.ref === "string" ? args.ref.trim() : ""
    try {
      const created = await actions.addComponent(componentName)
      if (ref) refMap.set(ref, created.id)
      return {
        content: JSON.stringify({ ok: true, id: created.id, ref: ref || undefined, name: created.name }),
        label: `Added ${created.name} (#${created.id})`,
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      return {
        content: JSON.stringify({ ok: false, error: msg }),
        label: `Failed to add "${componentName}": ${msg}`,
      }
    }
  }

  if (name === "remove_component") {
    const resolved = resolveNodeId(args.id, refMap)
    if (!resolved.ok) {
      return {
        content: JSON.stringify({ ok: false, error: resolved.error }),
        label: `remove_component failed: ${resolved.error}`,
      }
    }
    const id = resolved.id
    const removed = actions.removeComponent(id)
    if (!removed) {
      return {
        content: JSON.stringify({ ok: false, error: `No node with id ${id}.` }),
        label: `Could not remove #${id} (not found)`,
      }
    }
    return {
      content: JSON.stringify({ ok: true, id }),
      label: `Removed node #${id}`,
    }
  }

  if (name === "set_component_name") {
    const resolved = resolveNodeId(args.id, refMap)
    if (!resolved.ok) {
      return {
        content: JSON.stringify({ ok: false, error: resolved.error }),
        label: `set_component_name failed: ${resolved.error}`,
      }
    }
    const id = resolved.id
    if (typeof args.name !== "string") {
      return {
        content: JSON.stringify({ ok: false, error: "`name` must be a string (use \"\" to clear)." }),
        label: "set_component_name failed: missing name",
      }
    }
    const result = actions.setComponentName(id, args.name)
    if (!result.ok) {
      return {
        content: JSON.stringify({ ok: false, error: result.error }),
        label: `Could not rename #${id}: ${result.error}`,
      }
    }
    const display = args.name.trim() ? `"${args.name}"` : "(default)"
    return {
      content: JSON.stringify({ ok: true, id, name: args.name }),
      label: `Renamed #${id} → ${display}`,
    }
  }

  if (name === "set_control") {
    const resolved = resolveNodeId(args.id, refMap)
    if (!resolved.ok) {
      return {
        content: JSON.stringify({ ok: false, error: resolved.error }),
        label: `set_control failed: ${resolved.error}`,
      }
    }
    const id = resolved.id
    const controlName = typeof args.control === "string" ? args.control : ""
    if (!controlName) {
      return {
        content: JSON.stringify({ ok: false, error: "`control` is required." }),
        label: "set_control failed: missing control name",
      }
    }
    if (!("value" in args)) {
      return {
        content: JSON.stringify({ ok: false, error: "`value` is required." }),
        label: "set_control failed: missing value",
      }
    }

    const result = actions.setControl(id, controlName, args.value)
    if (!result.ok) {
      return {
        content: JSON.stringify({ ok: false, error: result.error }),
        label: `Could not set #${id}.${controlName}: ${result.error}`,
      }
    }
    const valueLabel =
      typeof args.value === "string" ? `"${args.value}"` :
      Array.isArray(args.value) ? `[${args.value.join(", ")}]` :
      String(args.value)
    return {
      content: JSON.stringify({ ok: true, id, control: controlName, value: args.value }),
      label: `Set #${id}.${controlName} → ${valueLabel}`,
    }
  }

  if (name === "connect_nodes" || name === "disconnect_nodes") {
    const parsed = parseEdgeArgs(args, refMap)
    if (!parsed.ok) {
      return {
        content: JSON.stringify({ ok: false, error: parsed.error }),
        label: `${name} failed: ${parsed.error}`,
      }
    }
    const { fromId, fromOutput, toId, toInput } = parsed
    const verb = name === "connect_nodes" ? "Connected" : "Disconnected"
    const failVerb = name === "connect_nodes" ? "connect" : "disconnect"
    const arrow = `#${fromId}.${fromOutput} -> #${toId}.${toInput}`

    const result =
      name === "connect_nodes"
        ? actions.connect(fromId, fromOutput, toId, toInput)
        : actions.disconnect(fromId, fromOutput, toId, toInput)

    if (!result.ok) {
      return {
        content: JSON.stringify({ ok: false, error: result.error }),
        label: `Could not ${failVerb} ${arrow}: ${result.error}`,
      }
    }
    return {
      content: JSON.stringify({ ok: true, from_id: fromId, from_output: fromOutput, to_id: toId, to_input: toInput }),
      label: `${verb} ${arrow}`,
    }
  }

  if (name === "set_layer_style") {
    const resolved = resolveNodeId(args.id, refMap)
    if (!resolved.ok) {
      return {
        content: JSON.stringify({ ok: false, error: resolved.error }),
        label: `set_layer_style failed: ${resolved.error}`,
      }
    }
    const id = resolved.id

    const hasFill = typeof args.fill === "string" && args.fill.trim().length > 0
    const hasFalse = "false_color" in args && args.false_color != null
    const hasTrue = "true_color" in args && args.true_color != null
    const hasLabels =
      args.label_colors != null &&
      typeof args.label_colors === "object" &&
      !Array.isArray(args.label_colors) &&
      Object.keys(args.label_colors as object).length > 0

    if (!hasFill && !hasFalse && !hasTrue && !hasLabels) {
      return {
        content: JSON.stringify({
          ok: false,
          error:
            "Provide at least one of `fill` (numeric), `false_color`/`true_color` (boolean), or `label_colors` (categorical).",
        }),
        label: "set_layer_style failed: nothing to apply",
      }
    }

    const style: LayerStyleUpdate = {}

    if (hasFill) {
      const fill = String(args.fill).trim()
      if (!VALID_LAYER_FILLS.has(fill)) {
        return {
          content: JSON.stringify({
            ok: false,
            error: `Unknown fill "${fill}". Choose one of: ${LAYER_FILL_OPTIONS.join(", ")}.`,
          }),
          label: `set_layer_style failed: unknown fill "${fill}"`,
        }
      }
      style.fill = fill
    }

    if (hasFalse) {
      const parsed = parseAgentColor(args.false_color)
      if (!parsed.ok) {
        return {
          content: JSON.stringify({ ok: false, error: `false_color: ${parsed.error}` }),
          label: `set_layer_style failed: ${parsed.error}`,
        }
      }
      style.falseColor = parsed.color
    }

    if (hasTrue) {
      const parsed = parseAgentColor(args.true_color)
      if (!parsed.ok) {
        return {
          content: JSON.stringify({ ok: false, error: `true_color: ${parsed.error}` }),
          label: `set_layer_style failed: ${parsed.error}`,
        }
      }
      style.trueColor = parsed.color
    }

    if (hasLabels) {
      const labelColors: Record<string, LayerStyleRgba> = {}
      for (const [label, raw] of Object.entries(args.label_colors as Record<string, unknown>)) {
        const parsed = parseAgentColor(raw)
        if (!parsed.ok) {
          return {
            content: JSON.stringify({ ok: false, error: `label_colors["${label}"]: ${parsed.error}` }),
            label: `set_layer_style failed: bad colour for "${label}"`,
          }
        }
        labelColors[label] = parsed.color
      }
      style.labelColors = labelColors
    }

    const result = actions.setLayerStyle(id, style)
    if (!result.ok) {
      return {
        content: JSON.stringify({ ok: false, error: result.error }),
        label: `Could not style #${id}: ${result.error}`,
      }
    }

    const parts: string[] = []
    if (style.fill) parts.push(`fill=${style.fill}`)
    if (style.falseColor) parts.push("false")
    if (style.trueColor) parts.push("true")
    if (style.labelColors) parts.push(`${Object.keys(style.labelColors).length} labels`)
    return {
      content: JSON.stringify({ ok: true, id, ...args }),
      label: `Styled Map layer #${id} (${parts.join(", ")})`,
    }
  }

  if (name === "set_layer_description") {
    const resolved = resolveNodeId(args.id, refMap)
    if (!resolved.ok) {
      return {
        content: JSON.stringify({ ok: false, error: resolved.error }),
        label: `set_layer_description failed: ${resolved.error}`,
      }
    }
    if (typeof args.description !== "string") {
      return {
        content: JSON.stringify({ ok: false, error: "Provide `description` as a string. Use \"\" to clear it." }),
        label: "set_layer_description failed: missing description",
      }
    }
    const id = resolved.id
    const description = args.description
    const result = actions.setLayerDescription(id, description)
    if (!result.ok) {
      return {
        content: JSON.stringify({ ok: false, error: result.error }),
        label: `Could not set description on #${id}: ${result.error}`,
      }
    }
    const cleared = description.trim() === ""
    return {
      content: JSON.stringify({ ok: true, id, description }),
      label: cleared ? `Cleared description on #${id}` : `Set description on #${id}`,
    }
  }

  if (name === "search_projects") {
    if (typeof actions.searchProjects !== "function") {
      return {
        content: JSON.stringify({ ok: false, error: "Project search is not available." }),
        label: "search_projects unavailable",
      }
    }
    const query = typeof args.query === "string" ? args.query.trim() : ""
    try {
      const projects = await actions.searchProjects(query)
      return {
        content: JSON.stringify({
          ok: true,
          query,
          count: projects.length,
          projects,
        }),
        label:
          projects.length === 0
            ? query
              ? `No projects matching "${query}"`
              : "No team projects found"
            : `Found ${projects.length} project${projects.length === 1 ? "" : "s"}${
                query ? ` matching "${query}"` : ""
              }`,
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      return {
        content: JSON.stringify({ ok: false, error: msg }),
        label: `search_projects failed: ${msg}`,
      }
    }
  }

  if (name === "read_project_model") {
    if (typeof actions.readProjectModel !== "function") {
      return {
        content: JSON.stringify({ ok: false, error: "Reading other projects is not available." }),
        label: "read_project_model unavailable",
      }
    }
    const projectId = Number(args.project_id)
    if (!Number.isFinite(projectId) || projectId <= 0) {
      return {
        content: JSON.stringify({ ok: false, error: "`project_id` must be a positive integer." }),
        label: "read_project_model failed: bad project_id",
      }
    }
    try {
      const result = await actions.readProjectModel(projectId)
      if (!result.ok) {
        return {
          content: JSON.stringify({ ok: false, error: result.error }),
          label: `Could not read project #${projectId}: ${result.error}`,
        }
      }
      return {
        content: JSON.stringify({
          ok: true,
          id: result.id,
          name: result.name,
          model: result.model,
        }),
        label: `Read project #${result.id} "${result.name}"`,
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      return {
        content: JSON.stringify({ ok: false, error: msg }),
        label: `read_project_model failed: ${msg}`,
      }
    }
  }

  if (name === "switch_extent") {
    return executeSwitchExtent(args, actions)
  }

  return {
    content: JSON.stringify({ ok: false, error: `Unknown tool: ${name}` }),
    label: `Unknown tool: ${name}`,
  }
}

export const SWITCH_EXTENT_TOOL = {
  type: "function" as const,
  function: {
    name: "switch_extent",
    description:
      "Switch the area the public map and model use. Pass an id from " +
      "`=== Areas the map can show ===`. The map refits and the model runs " +
      "again for that boundary. Use id null only for the project's own area " +
      "when the list shows id null.",
    parameters: {
      type: "object",
      properties: {
        id: {
          type: ["integer", "null"],
          description: "Area id from the areas list. Null selects the project's own area when that entry's id is null.",
        },
        name: {
          type: "string",
          description: "Area name, used when id is omitted. Matched against the areas list.",
        },
      },
    },
  },
}

/** Editor turns never see `switch_extent`. Public turns see it when at least two areas are offered. */
export function agentToolsForTurn(publicView: boolean, actions?: AgentActions) {
  if (!publicView) return AGENT_TOOLS
  const areas = actions?.listPublicExtents?.() ?? []
  if (areas.length < 2) return AGENT_TOOLS
  return [...AGENT_TOOLS, SWITCH_EXTENT_TOOL]
}

function executeSwitchExtent(
  args: Record<string, unknown>,
  actions: AgentActions
): { content: string; label?: string } {
  const areas = actions.listPublicExtents?.() ?? []
  if (areas.length < 2 || !actions.switchPublicExtent) {
    return {
      content: JSON.stringify({ ok: false, error: "This public project has no areas to switch between." }),
      label: "switch_extent failed: no areas",
    }
  }

  const named = typeof args.name === "string" ? args.name.trim().toLowerCase() : ""
  let target: { id: number | null, name: string, current: boolean } | undefined
  if (args.id === null) {
    target = areas.find(area => area.id == null)
  } else if (typeof args.id === "number" && Number.isInteger(args.id)) {
    target = areas.find(area => area.id === args.id)
  } else if (typeof args.id === "string" && /^-?\d+$/.test(args.id.trim())) {
    const id = Number(args.id)
    target = areas.find(area => area.id === id)
  } else if (named) {
    const exact = areas.filter(area => area.name.toLowerCase() === named)
    const matches = exact.length > 0 ? exact : areas.filter(area => area.name.toLowerCase().includes(named))
    if (matches.length > 1) {
      return {
        content: JSON.stringify({
          ok: false,
          error: `More than one area matches "${args.name}". Pass an id.`,
          areas: matches.map(area => ({ id: area.id, name: area.name })),
        }),
        label: "switch_extent failed: ambiguous area",
      }
    }
    target = matches[0]
  }

  if (!target) {
    return {
      content: JSON.stringify({
        ok: false,
        error: "Pass an area id or name from the areas list.",
        areas: areas.map(area => ({ id: area.id, name: area.name })),
      }),
      label: "switch_extent failed: unknown area",
    }
  }

  const result = actions.switchPublicExtent(target.id)
  if (!result.ok) {
    return {
      content: JSON.stringify(result),
      label: `switch_extent failed: ${result.error}`,
    }
  }
  return {
    content: JSON.stringify({ ok: true, id: result.id, name: result.name, unchanged: result.unchanged === true }),
    label: result.unchanged ? `Already showing ${result.name}` : `Switched area to ${result.name}`,
  }
}

const AGENT_TOOL_NAME_SET = new Set([
  ...AGENT_TOOLS.map(tool => tool.function.name),
  SWITCH_EXTENT_TOOL.function.name,
])

function stableArgsJson(args: unknown): string {
  let value = args
  if (typeof value === "string") {
    try {
      value = JSON.parse(value)
    } catch {
      return value as string
    }
  }
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const sorted: Record<string, unknown> = {}
    for (const key of Object.keys(value as Record<string, unknown>).sort()) {
      sorted[key] = (value as Record<string, unknown>)[key]
    }
    return JSON.stringify(sorted)
  }
  return JSON.stringify(value ?? {})
}

function recoveredToolCall(name: string, args: unknown, index: number): AgentToolCall {
  return {
    id: `recovered-${index}-${Date.now()}`,
    type: "function",
    function: { name, arguments: stableArgsJson(args) },
  }
}

function toolCallFromUnknown(raw: unknown, index: number): AgentToolCall | null {
  if (!raw || typeof raw !== "object") return null
  const rec = raw as Record<string, any>
  let name = ""
  let args: unknown = {}
  if (typeof rec.function === "string") {
    name = rec.function
    args = rec.arguments ?? rec.parameters ?? rec.input ?? {}
  } else if (rec.function && typeof rec.function === "object") {
    name = typeof rec.function.name === "string" ? rec.function.name : ""
    args = rec.function.arguments ?? rec.arguments ?? rec.parameters ?? {}
  } else if (typeof rec.name === "string") {
    name = rec.name
    args = rec.arguments ?? rec.parameters ?? rec.input ?? {}
  }
  name = name.trim()
  if (!name || !AGENT_TOOL_NAME_SET.has(name)) return null
  return recoveredToolCall(name, args, index)
}

function coerceToolParamValue(raw: string): unknown {
  const value = raw.trim()
  if (value === "true") return true
  if (value === "false") return false
  if (value === "null") return null
  if (value === "") return ""
  if (/^-?\d+$/.test(value)) return Number(value)
  if (/^-?\d+\.\d+$/.test(value)) return Number(value)
  if (
    (value.startsWith("{") && value.endsWith("}")) ||
    (value.startsWith("[") && value.endsWith("]"))
  ) {
    try {
      return JSON.parse(value)
    } catch {
      return raw
    }
  }
  return raw
}

function extractBalancedJson(text: string, from: number, open: "{" | "["): string | null {
  const start = text.indexOf(open, from)
  if (start < 0) return null
  const close = open === "{" ? "}" : "]"
  let depth = 0
  for (let i = start; i < text.length; i++) {
    const ch = text[i]
    if (ch === open) depth++
    else if (ch === close) {
      depth--
      if (depth === 0) return text.slice(start, i + 1)
    }
  }
  return text.slice(start) + close.repeat(Math.max(depth, 1))
}

function extractBalancedJsonObject(text: string, from = 0): string | null {
  return extractBalancedJson(text, from, "{")
}

function collectCallsFromParsed(parsed: unknown, into: AgentToolCall[]): void {
  if (!parsed) return
  const list = Array.isArray(parsed)
    ? parsed
    : Array.isArray((parsed as any).tool_calls)
      ? (parsed as any).tool_calls
      : [parsed]
  for (const item of list) {
    const call = toolCallFromUnknown(item, into.length)
    if (call) into.push(call)
  }
}

/**
 * gpt-oss (and others) often dump the OpenAI payload as plain JSON in
 * `reasoning` / `content` instead of filling `message.tool_calls`:
 *   { "tool_calls": [ { "function": "add_component", "arguments": {…} } ] }
 * Also accepts the nested `{ function: { name, arguments } }` shape and a
 * bare array of those objects.
 */
export function parseJsonToolCalls(text: string): AgentToolCall[] {
  if (!text) return []
  const calls: AgentToolCall[] = []

  let searchFrom = 0
  while (searchFrom < text.length) {
    const keyIdx = text.indexOf('"tool_calls"', searchFrom)
    if (keyIdx < 0) break
    let start = -1
    for (let i = keyIdx; i >= 0; i--) {
      if (text[i] === "{") {
        start = i
        break
      }
    }
    if (start < 0) {
      searchFrom = keyIdx + 12
      continue
    }
    const json = extractBalancedJsonObject(text, start)
    if (!json) {
      searchFrom = keyIdx + 12
      continue
    }
    try {
      collectCallsFromParsed(JSON.parse(json), calls)
    } catch {
      // ignore unreadable blobs
    }
    searchFrom = start + json.length
  }

  return calls
}

function parseKwargList(src: string): Record<string, unknown> {
  const args: Record<string, unknown> = {}
  const re =
    /([A-Za-z_][\w]*)\s*=\s*("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|true|false|null|-?\d+(?:\.\d+)?|\[[^\]]*\]|\{[^}]*\}|[^\s,]+)/g
  let match: RegExpExecArray | null
  while ((match = re.exec(src)) !== null) {
    let raw = match[2]
    if (
      (raw.startsWith('"') && raw.endsWith('"')) ||
      (raw.startsWith("'") && raw.endsWith("'"))
    ) {
      raw = raw.slice(1, -1)
    }
    args[match[1]] = coerceToolParamValue(raw)
  }
  return args
}

/**
 * Programmatic plan lines from the system prompt / `<plan>` block:
 *   add_component(name="UKCEH Land Cover", ref="x1")
 *   x2 = add_component("Map layer")
 */
export function parsePlanNotationToolCalls(text: string): AgentToolCall[] {
  if (!text) return []
  const names = Array.from(AGENT_TOOL_NAME_SET).join("|")
  const re = new RegExp(
    `(?:([A-Za-z_][\\w-]*)\\s*=\\s*)?(${names})\\s*\\(([^\\)]*)\\)`,
    "g"
  )
  const calls: AgentToolCall[] = []
  let match: RegExpExecArray | null
  while ((match = re.exec(text)) !== null) {
    const ref = match[1]
    const name = match[2]
    const inside = match[3].trim()
    let args = parseKwargList(inside)
    if (Object.keys(args).length === 0 && inside) {
      const positional = inside.match(/^["']([^"']+)["']$/)
      if (positional && name === "add_component") {
        args = { name: positional[1] }
      }
    }
    if (ref && name === "add_component" && args.ref == null) args.ref = ref
    if (Object.keys(args).length === 0 && name !== "search_projects") continue
    calls.push(recoveredToolCall(name, args, calls.length))
  }
  return calls
}

/** True when the model dumped tools as JSON/XML in text instead of the API field. */
export function textHasUnexecutedToolIntent(text: string): boolean {
  if (!text) return false
  return /"tool_calls"\s*:/.test(text) || /<(function|tool_call)\b/i.test(text)
}

function dedupeToolCalls(calls: AgentToolCall[]): AgentToolCall[] {
  const seen = new Set<string>()
  const out: AgentToolCall[] = []
  for (const call of calls) {
    const name = call.function?.name
    if (!name) continue
    const key = `${name}:${call.function?.arguments ?? ""}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(call)
  }
  return out
}

/**
 * Qwen 3.5 / 3.6 (and Qwen3-coder) XML tool calls, which Groq sometimes
 * fails to lift into `message.tool_calls` — especially when they leak
 * into `reasoning` or a `<think>` block:
 *   <tool_call>
 *     <function=add_component>
 *       <parameter=name>SPAM</parameter>
 *       <parameter=ref>x1</parameter>
 *     </function>
 *   </tool_call>
 */
export function parseQwenXmlToolCalls(text: string): AgentToolCall[] {
  if (!text) return []
  const calls: AgentToolCall[] = []
  const fnRe = /<function=([A-Za-z_][\w-]*)\s*>([\s\S]*?)<\/function>/g
  let match: RegExpExecArray | null
  while ((match = fnRe.exec(text)) !== null) {
    const name = match[1]
    const body = match[2]
    const args: Record<string, unknown> = {}
    const paramRe = /<parameter=([A-Za-z_][\w-]*)\s*>([\s\S]*?)<\/parameter>/g
    let param: RegExpExecArray | null
    while ((param = paramRe.exec(body)) !== null) {
      args[param[1]] = coerceToolParamValue(param[2])
    }
    const namedRe = /<parameter\s+name=["']([^"']+)["']\s*>([\s\S]*?)<\/parameter>/g
    while ((param = namedRe.exec(body)) !== null) {
      args[param[1]] = coerceToolParamValue(param[2])
    }
    calls.push(recoveredToolCall(name, args, calls.length))
  }
  return calls
}

/**
 * Hermes / Qwen JSON tool-call blocks:
 *   <tool_call>{"name":"add_component","arguments":{"name":"SPAM"}}</tool_call>
 * Bodies that already contain `<function=` are left to parseQwenXmlToolCalls.
 */
export function parseHermesToolCalls(text: string): AgentToolCall[] {
  if (!text) return []
  const calls: AgentToolCall[] = []
  const re = /<tool_call>\s*([\s\S]*?)<\/tool_call>/g
  let match: RegExpExecArray | null
  while ((match = re.exec(text)) !== null) {
    const body = match[1].trim()
    if (!body || /<function=/i.test(body)) continue
    const json = extractBalancedJsonObject(body)
    if (!json) continue
    try {
      const parsed = JSON.parse(json)
      const name = parsed?.name || parsed?.function?.name
      if (typeof name !== "string" || !name.trim()) continue
      const args = parsed.arguments ?? parsed.parameters ?? parsed.input ?? {}
      calls.push(recoveredToolCall(name, args, calls.length))
    } catch {
      // ignore unreadable blocks
    }
  }
  return calls
}

/**
 * Best-effort parser for Llama-3.x's *native* tool-call syntax, which
 * Groq's OpenAI-compat layer rejects. The model occasionally emits
 *   `<function=name{"key": "value"}</function>`
 * (or variations: with `>`, without `</function>`, with multiple calls
 * back-to-back, with markdown fences around the block, etc.) instead of
 * a proper `tool_calls` JSON array. We pull each `<function=…` block out
 * of the failed generation, isolate the first balanced `{ … }` JSON
 * object after the name, and synthesise an `AgentToolCall` so the agent
 * loop can execute it as if Groq had returned it normally.
 *
 * Qwen XML (`<function=name><parameter=…>`) is skipped here so we don't
 * invent empty-arg calls; use `parseQwenXmlToolCalls` / `recoverToolCallsFromText`.
 *
 * Returns an empty array if nothing parseable is found; the caller
 * falls back to its existing corrective-retry path in that case.
 */
export function parseLlamaNativeToolCalls(text: string): AgentToolCall[] {
  if (!text) return []
  const calls: AgentToolCall[] = []
  // `s` flag lets `.` cross newlines; `.*?` is lazy so multiple
  // back-to-back `<function=…` blocks parse independently.
  const re = /<function=([A-Za-z_][\w-]*)\s*([\s\S]*?)(?:<\/function>|$)/g
  let match: RegExpExecArray | null
  while ((match = re.exec(text)) !== null) {
    const name = match[1]
    let tail = match[2].trim()
    // Some variants put a `>` between the name and the args object,
    // others go straight into `{`. Strip a leading `>` if present.
    if (tail.startsWith(">")) tail = tail.slice(1).trim()

    // Qwen XML uses `<parameter=` children, not a JSON object. Leave
    // those to parseQwenXmlToolCalls so we don't emit `{}` and fail later.
    if (/<parameter[= ]/i.test(tail)) continue

    const start = tail.indexOf("{")
    if (start < 0) {
      // No-arg call (e.g. search_projects) is fine; required-arg tools
      // still go through the dispatcher and report a soft error.
      calls.push(recoveredToolCall(name, {}, calls.length))
      continue
    }

    const argsJson = extractBalancedJsonObject(tail, start) ?? "{}"
    calls.push(recoveredToolCall(name, argsJson, calls.length))
  }
  return calls
}

/**
 * Recover structured tool calls from model text when Groq/Qwen emit the
 * call in `content` or `reasoning` instead of `message.tool_calls`.
 */
export function recoverToolCallsFromText(text: string): AgentToolCall[] {
  if (!text) return []
  const jsonCalls = parseJsonToolCalls(text)
  return dedupeToolCalls([
    ...jsonCalls,
    ...parseQwenXmlToolCalls(text),
    ...parseHermesToolCalls(text),
    ...parseLlamaNativeToolCalls(text),
    // Plan notation is a fallback when the model wrote the programmatic
    // plan but never emitted JSON / XML / API tool_calls.
    ...(jsonCalls.length > 0 ? [] : parsePlanNotationToolCalls(text)),
  ])
}

function stripJsonToolCallBlobs(text: string): string {
  let out = text
  let searchFrom = 0
  while (searchFrom < out.length) {
    const keyIdx = out.indexOf('"tool_calls"', searchFrom)
    if (keyIdx < 0) break
    let start = -1
    for (let i = keyIdx; i >= 0; i--) {
      if (out[i] === "{") {
        start = i
        break
      }
    }
    if (start < 0) {
      searchFrom = keyIdx + 12
      continue
    }
    const json = extractBalancedJsonObject(out, start)
    if (!json) {
      searchFrom = keyIdx + 12
      continue
    }
    out = out.slice(0, start) + out.slice(start + json.length)
    searchFrom = start
  }
  return out
}

/** Strip native / XML / JSON tool-call markup so it never reaches the chat bubble. */
export function stripNativeToolMarkup(text: string): string {
  if (!text) return text
  return stripJsonToolCallBlobs(text)
    .replace(/<tool_call>[\s\S]*?<\/tool_call>/g, "")
    .replace(/<function_invocation>[\s\S]*?<\/function_invocation>/g, "")
    .replace(/<function=[A-Za-z_][\w-]*\s*>[\s\S]*?<\/function>/g, "")
    .replace(/<function=[A-Za-z_][\w-]*\s*\{[\s\S]*?<\/function>/g, "")
    .replace(/<\/?(?:tool_call|function_invocation|function)\b[^>]*>/g, "")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
}

/**
 * Coerce / validate the four edge-endpoint arguments shared by
 * `connect_nodes` and `disconnect_nodes`. Returns either the cleaned
 * values or a single `error` string ready to forward to the model.
 */
export function parseEdgeArgs(args: Record<string, unknown>, refMap: Map<string, number>):
  | { ok: true; fromId: number; fromOutput: string; toId: number; toInput: string }
  | { ok: false; error: string } {
  const from = resolveNodeId(args.from_id, refMap)
  if (!from.ok) return { ok: false, error: `from_id: ${from.error}` }
  const to = resolveNodeId(args.to_id, refMap)
  if (!to.ok) return { ok: false, error: `to_id: ${to.error}` }
  const fromOutput = typeof args.from_output === "string" ? args.from_output : ""
  const toInput = typeof args.to_input === "string" ? args.to_input : ""

  if (!fromOutput) return { ok: false, error: "`from_output` is required." }
  if (!toInput) return { ok: false, error: "`to_input` is required." }
  return { ok: true, fromId: from.id, fromOutput, toId: to.id, toInput }
}


